import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { LazyImage } from "@/components/ui/lazy-image";
import { Star, Users, DollarSign, Award, Calendar, Menu, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const Top20Rankings2025 = () => {
  const [activeSection, setActiveSection] = useState<string>("introduction");
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const tocScrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Vendor data with Assembly as first entry
  const vendors = [
    {
      id: "vendor-1",
      rank: 1,
      name: "Assembly",
      logo: "/placeholder.svg",
      about: "Assembly is a recognition and engagement platform built to work for organizations of any size. With a 4.9/5 G2 rating from 3,000+ reviews, it combines peer-to-peer recognition, automated rewards, and engagement tools in one easy-to-use system.",
      keyFeatures: [
        "Peer & Manager Recognition: Shoutouts, badges, anniversaries, and peer nominations make appreciation visible across the team.",
        "Automated Rewards: Deliver gift cards, swag, or discount codes automatically based on participation or milestones.", 
        "Engagement Automations: Launch onboarding recognition, surveys, challenges, or social announcements via no-code workflows.",
        "Gifting-Only Option: Send curated, branded gifts to anyone (clients, candidates, staff) without a full program—recipients choose, you only pay on redemption.",
        "Scalable Pricing: Start with a free plan, scale to $2 or $3/user/month, or move to enterprise-grade Digital HQ as needed.",
        "Seamless Integrations: Works with Slack, Teams, HRIS/SSO systems, with budgeting and approvals built in.",
        "Insightful Reporting: Monitor participation, spend, sentiment, and ROI in real time."
      ],
      gifting: "Assembly also supports gifting-only use cases—ideal for sending branded, trackable gifts to employees, customers, candidates, or partners. Recipients pick from curated catalogs, and companies pay only for redeemed items.",
      pricing: {
        free: "Start with gifting-only or basic recognition; pay only for rewards sent",
        recognition: "$2 per employee/month",
        engagement: "$3 per employee/month", 
        enterprise: "Contact sales"
      },
      whyChoose: "High adoption, fast rollout, and unmatched flexibility—whether starting with free gifting or scaling to full recognition and engagement.",
      link: "https://joinassembly.com/",
      g2Score: "4.9",
      screenshot: "/lovable-uploads/vendors/assembly.webp"
    },
    {
      id: "vendor-2",
      rank: 2,
      name: "Awardco",
      about: "Awardco makes recognizing and rewarding people seamless by offering one of the largest reward catalogs in the industry. Their focus is on providing employees with choice — from gift cards and swag to charitable donations and experiences.",
      bestFor: "Mid-sized to enterprise companies (100–10,000 employees)",
      customers: ["Zillow", "Pinterest", "JPMorgan Chase"],
      pricing: {
        smb: "$2,500/year for service awards and manager notifications",
        recognition: "$4,000/year for peer-to-peer recognition",
        enterprise: "Contact sales"
      },
      keyFeatures: [
        "Vast rewards library including Amazon and charities",
        "Customizable recognition workflows",
        "Service anniversary and birthday automation"
      ],
      link: "https://www.award.co/",
      screenshot: "/lovable-uploads/vendors/awardco.webp"
    },
    {
      id: "vendor-3",
      rank: 3,
      name: "Kudos",
      about: "Kudos offers a comprehensive engagement platform that blends recognition with analytics, pulse surveys, and culture-building features. Their focus is on enterprise adoption with an emphasis on culture measurement.",
      bestFor: "Larger companies (500+ employees)",
      customers: ["DHL", "Chewy", "Make a Wish Foundation"],
      pricing: "Contact sales",
      keyFeatures: [
        "Recognition and rewards marketplace",
        "Awards and nominations",
        "People analytics and reporting",
        "Pulse surveys for engagement tracking",
        "Galleries to highlight cultural moments"
      ],
      link: "https://www.kudos.com/",
      screenshot: "/lovable-uploads/vendors/kudos.webp"
    },
    {
      id: "vendor-4",
      rank: 4,
      name: "Bonusly",
      about: "Bonusly builds high-performing cultures through real-time recognition and feedback. Known as an early pioneer in the recognition and rewards space, Bonusly emphasizes participation and analytics to maximize impact.",
      bestFor: "Businesses of all sizes, from startups to enterprises",
      customers: ["Chobani", "Hulu", "ZipRecruiter"],
      pricing: {
        core: "$3 per user/month + rewards",
        pro: "$5 per user/month + rewards",
        enterprise: "Contact sales"
      },
      keyFeatures: [
        "Real-time recognition and feedback",
        "Social feeds to amplify wins",
        "Reporting and analytics for HR teams",
        "Integrations with Slack, MS Teams, and more"
      ],
      link: "https://bonusly.com/",
      screenshot: "/lovable-uploads/vendors/bonusly.webp"
    },
    {
      id: "vendor-5",
      rank: 5,
      name: "Motivosity",
      about: "Motivosity combines recognition and rewards with performance management features like 1:1s, surveys, and reviews. Its holistic approach makes it a popular choice for companies that want culture-building and performance tools under one roof.",
      bestFor: "Companies with 10–1,000 employees",
      customers: ["KPMG", "Bosch", "Cotopaxi", "FujiFilm"],
      pricing: {
        standalone: "$2 for each standalone tier (Community, Recognition, Manager Development, Insights)",
        fullSuite: "$8 for the full suite"
      },
      keyFeatures: [
        "Recognition and rewards automation",
        "1:1 meeting tools and performance reviews",
        "Culture-focused connection features",
        "Strong survey and feedback tools"
      ],
      link: "https://www.motivosity.com/",
      screenshot: "/lovable-uploads/vendors/motivosity.webp"
    },
    {
      id: "vendor-6",
      rank: 6,
      name: "Nectar",
      about: "Nectar provides a streamlined recognition platform that makes it easy to reward employees, reinforce core values, and track engagement. With wellness incentives and Amazon integration, Nectar is a popular choice for companies looking for fast implementation.",
      bestFor: "SMBs and mid-sized businesses (50–2,000 employees)",
      customers: ["Redfin", "MLB", "Golden State Warriors", "Heineken"],
      pricing: {
        standard: "$2.75 per employee/month",
        plus: "Adds SSO, Amazon integration, and account management"
      },
      keyFeatures: [
        "Recognition tied to company values",
        "Wellness incentives built-in",
        "Simple, intuitive user interface",
        "Quick setup and no contracts"
      ],
      link: "https://nectarhr.com/",
      screenshot: "/lovable-uploads/vendors/nectar.webp"
    },
    {
      id: "vendor-7",
      rank: 7,
      name: "Guusto",
      about: "Guusto focuses on frontline workers and distributed teams, making recognition and rewards accessible to employees who may not be desk-based. With 4,000+ app store reviews, it's trusted by organizations with large non-digital workforces.",
      bestFor: "Companies with 200+ employees, especially frontline-heavy industries",
      customers: ["The Body Shop", "Seaspan", "Pilgrim's"],
      pricing: {
        free: "Single user",
        lite: "$40/month for milestone automation",
        essential: "$120/month with a 30-seat minimum",
        premium: "$400/month with an 80-seat minimum"
      },
      keyFeatures: [
        "Simple gifting for non-desk employees",
        "Easy mobile redemption",
        "Milestone automation"
      ],
      link: "https://guusto.com/",
      screenshot: "/lovable-uploads/vendors/guusto.webp"
    },
    {
      id: "vendor-8",
      rank: 8,
      name: "WorkTango",
      about: "WorkTango is more than recognition — it's a full employee experience platform. Recognition, surveys, and goals/feedback are bundled or sold separately, making it a flexible enterprise-grade solution.",
      bestFor: "Companies with 200+ employees",
      customers: ["Pizza Pizza", "Habitat for Humanity", "VMLY&R"],
      pricing: {
        recognition: "$8,000 minimum",
        surveys: "$8,000 minimum",
        fullSuite: "$10,000 minimum"
      },
      keyFeatures: [
        "Recognition integrated with surveys and feedback",
        "Strong analytics and reporting",
        "Scalable for large organizations"
      ],
      link: "https://www.worktango.com/",
      screenshot: "/lovable-uploads/vendors/worktango.webp"
    },
    {
      id: "vendor-9",
      rank: 9,
      name: "Mo",
      about: "Mo helps teams build connections through recognition, rewards, and cultural moments. Their template library gives managers and employees easy ideas to celebrate each other, particularly valuable in hybrid and remote settings.",
      bestFor: "Mid-sized organizations looking to boost engagement",
      customers: ["AXOL", "Quark", "SGN"],
      pricing: {
        appreciation: "$2.25 per employee/month",
        engagement: "$4.50 per employee/month"
      },
      keyFeatures: [
        "Library of recognition templates",
        "Designed for remote engagement",
        "Rewards and recognition in one"
      ],
      link: "https://mo.work/",
      screenshot: "/lovable-uploads/vendors/mo.webp"
    },
    {
      id: "vendor-10",
      rank: 10,
      name: "Cooleaf",
      about: "Cooleaf focuses on integrating recognition into employees' daily tools. From Salesforce to LinkedIn Learning and Zendesk, recognition is tied to workflows for maximum adoption.",
      bestFor: "SMBs with 10–1,000 employees",
      customers: ["Shipt", "Acosta", "Aprio"],
      pricing: "Contact sales",
      keyFeatures: [
        "Deep integrations with business tools",
        "Combines recognition with learning and engagement",
        "Social-style feeds for recognition moments"
      ],
      link: "https://www.cooleaf.com/",
      screenshot: "/lovable-uploads/vendors/coolleaf.webp"
    },
    {
      id: "vendor-11",
      rank: 11,
      name: "Bravo",
      about: "Bravo brings AI-driven recognition to the space, making appreciation more personalized and impactful. With robust rewards in North America, Bravo is a good fit for small to mid-sized teams.",
      bestFor: "Teams of 5–200 employees",
      customers: ["Yelp", "LinkedIn", "Jira"],
      pricing: {
        small: "$39/month (0–10 employees)",
        medium: "$79/month (11–50 employees)",
        large: "$199/month (51–200 employees)",
        enterprise: "Contact sales (200+)"
      },
      keyFeatures: [
        "AI-enhanced recognition",
        "Robust regional reward system",
        "Simple setup"
      ],
      link: "https://getbravo.io/",
      screenshot: "/lovable-uploads/vendors/bravo.webp"
    },
    {
      id: "vendor-12",
      rank: 12,
      name: "Bucketlist",
      about: "Bucketlist focuses on making recognition highly personalized and memorable. Employees feel truly valued with tailored rewards and meaningful experiences.",
      bestFor: "Teams of 100–1,000 employees",
      customers: ["Orlando Magic", "First Bank", "Chick-fil-A"],
      pricing: "Contact sales",
      keyFeatures: [
        "Deep focus on personalized recognition",
        "Strong culture fit emphasis",
        "Memorable employee experiences"
      ],
      link: "https://bucketlistrewards.com/",
      screenshot: "/lovable-uploads/vendors/bucketlist.webp"
    },
    {
      id: "vendor-13",
      rank: 13,
      name: "Empuls",
      about: "Empuls is a full engagement suite with recognition, rewards, surveys, and a social intranet. It's designed to connect and celebrate teams globally.",
      bestFor: "Teams of 25+ employees",
      customers: ["Adobe", "Pepsi", "Grammarly", "Freshworks"],
      pricing: {
        recognition: "$3 per employee/month",
        surveys: "$3 per employee/month",
        socialIntranet: "$1 per employee/month",
        perks: "$2 per employee/month"
      },
      keyFeatures: [
        "Surveys, polls, and contests alongside recognition",
        "Extensive perks and benefits options",
        "Flexible modular pricing"
      ],
      link: "https://www.empuls.io/",
      screenshot: "/lovable-uploads/vendors/empuls.webp"
    },
    {
      id: "vendor-14",
      rank: 14,
      name: "Snappy Gift",
      about: "Snappy makes gifting easy by allowing recipients to choose their own rewards. It's as much about surprise and delight as it is about recognition.",
      bestFor: "Companies of all sizes, from startups to enterprise",
      customers: ["Meta", "Uber", "Microsoft", "Zynga"],
      pricing: {
        essential: "Free (pay only for redemptions)",
        elevated: "$999/year",
        enterprise: "Contact sales"
      },
      keyFeatures: [
        "Curated gift library",
        "Easy tracking and redemption",
        "Integrations with Slack, Teams, and HRIS systems"
      ],
      link: "https://www.snappy.com/",
      screenshot: "/lovable-uploads/vendors/snappy.webp"
    },
    {
      id: "vendor-15",
      rank: 15,
      name: "HeyTaco",
      about: "HeyTaco is a lightweight Slack and MS Teams plugin that brings recognition directly into chat workflows. With a fun, gamified approach (tacos as recognition currency), it's simple yet effective.",
      bestFor: "Small companies (3–100 employees)",
      customers: ["Buffer", "Axios", "Lynton"],
      pricing: "$3 per employee/month",
      keyFeatures: [
        "Gamified recognition (taco points)",
        "Direct Slack/Teams integration",
        "Extremely quick setup"
      ],
      link: "https://heytaco.com/",
      screenshot: "/lovable-uploads/vendors/heytaco.webp"
    },
    {
      id: "vendor-16",
      rank: 16,
      name: "Achievers",
      about: "Achievers partners with enterprise organizations to build a recognition culture rooted in core values. Their science-backed approach has the highest active usage rates in the industry, leading to measurable productivity and retention gains.",
      bestFor: "Enterprise companies with 1,000+ employees",
      customers: ["CVS Health", "Samsung", "GM", "Discover"],
      pricing: "Contact sales",
      keyFeatures: [
        "2.5x increase in retention reported by customers",
        "2x increase in career longevity",
        "2x higher productivity",
        "Strong analytics and reporting"
      ],
      link: "https://www.achievers.com/",
      screenshot: "/lovable-uploads/vendors/achievers.webp"
    },
    {
      id: "vendor-17",
      rank: 17,
      name: "Workhuman",
      about: "Workhuman is one of the oldest and most globally recognized platforms in the recognition space. Serving 180+ countries and 34 languages, it's designed for enterprises with large, distributed workforces.",
      bestFor: "Large enterprise organizations",
      customers: ["LinkedIn", "BP", "ADP", "Intuit"],
      pricing: "Contact sales",
      keyFeatures: [
        "Global rewards and recognition",
        "Milestones, social connections, consulting services",
        "Over 20 years of expertise in employee engagement"
      ],
      link: "https://www.workhuman.com/",
      screenshot: "/lovable-uploads/vendors/workhuman.webp"
    },
    {
      id: "vendor-18",
      rank: 18,
      name: "Fond",
      about: "Fond helps companies create cultures employees want to be a part of. Now integrated with Reward Gateway, it expands its enterprise engagement capabilities.",
      bestFor: "Companies with 500+ employees",
      customers: ["Salesforce", "Instacart", "PEGA"],
      pricing: "Contact sales",
      keyFeatures: [
        "Recognition + reward marketplace",
        "Perks and discounts",
        "Backed by Reward Gateway's enterprise infrastructure"
      ],
      link: "https://www.fond.co/",
      screenshot: "/lovable-uploads/vendors/fond.webp"
    },
    {
      id: "vendor-19",
      rank: 19,
      name: "Reward Gateway",
      about: "Reward Gateway is a full-suite engagement provider, offering recognition, rewards, wellness, surveys, and employee analytics for enterprise clients.",
      bestFor: "Enterprise customers",
      customers: ["Orbis", "C Space", "ABC Medical"],
      pricing: "Contact sales",
      keyFeatures: [
        "Full-suite engagement platform",
        "Recognition, rewards, wellness, surveys",
        "Employee analytics for enterprise"
      ],
      link: "https://www.rewardgateway.com/",
      screenshot: "/lovable-uploads/vendors/rewardgateway.webp"
    },
    {
      id: "vendor-20",
      rank: 20,
      name: "Vantage Circle",
      about: "Vantage Circle is a comprehensive employee engagement platform that combines recognition, rewards, discounts, surveys, and wellness programs into one ecosystem. Its mission is to drive engagement for large enterprises with complex global workforces.",
      bestFor: "Mid to large enterprises (500+ employees)",
      customers: ["DHL", "Wells Fargo", "Bosch"],
      pricing: {
        grow: "$2.69 per employee/month",
        transform: "Contact sales for advanced features"
      },
      keyFeatures: [
        "Recognition and rewards marketplace",
        "Corporate discounts program for employees",
        "Integrated wellness challenges (steps, fitness, health tracking)",
        "Pulse surveys and engagement analytics",
        "Gamification features to boost participation"
      ],
      whyStandOut: "Vantage Circle's biggest advantage is its breadth of features — recognition is seamlessly combined with wellness and discounts, making it ideal for enterprises looking to engage hard-to-reach employees.",
      link: "https://www.vantagecircle.com/",
      screenshot: "/lovable-uploads/vendors/vantagecircle.webp"
    }
  ];

  // Handle scroll for active section
  useEffect(() => {
    const sections = ['introduction', 'overview', 'methodology', ...vendors.map(v => v.id), 'top-pick-2026'];
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      // Clear previous timeout to debounce scroll events
      clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        const is2XL = window.innerWidth >= 1536;
        const stickyOffset = is2XL ? 112 : 96;
        const scrollPosition = window.scrollY + stickyOffset + 50; // Increased buffer for better detection

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i]);
          if (section && section.offsetTop <= scrollPosition) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }, 50); // Debounce scroll events
    };

    window.addEventListener('scroll', handleScroll);
    // Initial call to set active section on load
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Immediately set active section for better UX
      setActiveSection(id);
      
      // Calculate consistent offset for all sections
      const is2XL = window.innerWidth >= 1536;
      const stickyOffset = is2XL ? 112 : 96;
      const elementPosition = element.offsetTop - stickyOffset - 20; // Extra padding for better positioning
      
      window.scrollTo({
        top: Math.max(0, elementPosition), // Ensure we don't scroll to negative position
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Top 20 Best Employee Recognition Software (2026 Rankings) | Assembly</title>
        <meta name="description" content="Discover the top 20 employee recognition & rewards software in 2026. Compare features, pricing, and reviews to find the best platform for your team." />
        <meta name="keywords" content="best employee recognition software 2026, top recognition platforms, rewards software, employee engagement tools, compare recognition software" />
        <link rel="canonical" href="https://joinassembly.com/best/employee-recognition-software/2026" />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-orange-50 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/20 via-transparent to-orange-100/20"></div>
        <div className="absolute top-20 -right-20 w-96 h-96 bg-gradient-to-br from-assembly-blue/10 to-assembly-orange/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 -left-20 w-80 h-80 bg-gradient-to-tr from-assembly-orange/10 to-assembly-blue/10 rounded-full blur-3xl animate-float-reverse"></div>
        
        <div className="container mx-auto px-4 py-12 lg:py-20 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6 lg:space-y-8">
              <div className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-primary/10 text-primary text-xs lg:text-sm font-semibold rounded-full border border-primary/20 backdrop-blur-sm">
                <Award className="h-3 w-3 lg:h-4 lg:w-4 mr-2" />
                Employee Recognition Platforms 2026
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight">
                20 Best Employee Recognition Software Platforms in 2026
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <img 
                    src="/lovable-uploads/0386712e-468b-472b-a7a2-998211804463.png" 
                    alt="Jonathan Fields" 
                    className="w-8 h-8 lg:w-10 lg:h-10 rounded-full object-cover"
                  />
                  <span className="font-medium">Jonathan Fields</span>
                </div>
                <span className="hidden sm:block">•</span>
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Last Updated Jan 5, 2026
                </span>
              </div>
            </div>
            <div className="relative mt-8 lg:mt-0">
              <div className="aspect-[4/3] bg-gradient-to-br from-blue-100 to-orange-100 rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-700">
                <img 
                  src="/lovable-uploads/13ccae42-b41b-4186-b0ee-0e74f4d57aa1.png" 
                  alt="Happy employee celebrating success at work" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-2 sm:px-4 py-12">
        {/* Mobile Navigation Button */}
        {isMobile && (
          <div className="lg:hidden fixed top-20 left-2 z-30 mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="hidden md:block bg-background/95 backdrop-blur-sm shadow-lg border-2 text-xs"
            >
              {isMobileNavOpen ? <X className="h-3 w-3" /> : <Menu className="h-3 w-3" />}
              <span className="ml-1 text-xs">{isMobileNavOpen ? 'Close' : 'Menu'}</span>
            </Button>
          </div>
        )}

        {/* Mobile Navigation Overlay */}
        {isMobile && isMobileNavOpen && (
          <div className="lg:hidden fixed inset-0 z-20 bg-background/95 backdrop-blur-sm">
            <div className="pt-20 px-4 h-full overflow-y-auto">
              <Card className="shadow-xl">
                <CardHeader className="pb-3 bg-gradient-to-r from-assembly-blue/5 to-assembly-orange/5">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <div className="w-2 h-8 bg-gradient-to-b from-assembly-blue to-assembly-orange rounded-full"></div>
                    Jump to Section
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 p-4 max-h-[70vh] overflow-y-auto">
                  <button
                    onClick={() => {
                      scrollToSection('introduction');
                      setIsMobileNavOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 ${
                      activeSection === 'introduction'
                        ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    Introduction
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('overview');
                      setIsMobileNavOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 ${
                      activeSection === 'overview'
                        ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('methodology');
                      setIsMobileNavOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 ${
                      activeSection === 'methodology'
                        ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    Top 20 Recognition Platforms
                  </button>
                  <div className="border-t pt-3 mt-3">
                    {vendors.map((vendor) => (
                      <button
                        key={vendor.id}
                        onClick={() => {
                          scrollToSection(vendor.id);
                          setIsMobileNavOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm rounded-lg transition-all duration-200 ${
                          activeSection === vendor.id
                            ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                            : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                        }`}
                      >
                        <span className="font-medium text-assembly-orange mr-2">#{vendor.rank}</span>
                        {vendor.name}
                      </button>
                    ))}
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <button
                      onClick={() => {
                        scrollToSection('top-pick-2026');
                        setIsMobileNavOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 ${
                        activeSection === 'top-pick-2026'
                          ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                          : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                      }`}
                    >
                      Top Pick 2026
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Table of Contents Sidebar - Hidden on mobile */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 2xl:top-28 h-[calc(80vh-6rem)] 2xl:h-[calc(80vh-7rem)] min-h-[400px] z-20">
              <Card className="overflow-hidden shadow-lg border-0 bg-background h-full flex flex-col pointer-events-auto">
                <CardHeader className="pb-3 bg-gradient-to-r from-assembly-blue/5 to-assembly-orange/5 flex-shrink-0">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <div className="w-2 h-8 bg-gradient-to-b from-assembly-blue to-assembly-orange rounded-full"></div>
                    Table of Contents
                  </CardTitle>
                </CardHeader>
                <CardContent ref={tocScrollRef} className="space-y-1 p-4 overflow-y-auto flex-1">
                  <button
                    data-section="introduction"
                    onClick={() => scrollToSection('introduction')}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 relative ${
                      activeSection === 'introduction'
                        ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    Introduction
                  </button>
                  <button
                    data-section="overview"
                    onClick={() => scrollToSection('overview')}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 relative ${
                      activeSection === 'overview'
                        ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    data-section="methodology"
                    onClick={() => scrollToSection('methodology')}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 relative ${
                      activeSection === 'methodology'
                        ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    Top 20 Recognition Platforms
                  </button>
                  <div className="border-t pt-3 mt-3">
                    {vendors.map((vendor) => (
                      <button
                        key={vendor.id}
                        data-section={vendor.id}
                        onClick={() => scrollToSection(vendor.id)}
                        className={`w-full text-left px-4 py-2 text-sm rounded-lg transition-all duration-200 relative ${
                          activeSection === vendor.id
                            ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                            : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                        }`}
                      >
                        <span className="font-medium text-assembly-orange mr-2">#{vendor.rank}</span>
                        {vendor.name}
                      </button>
                    ))}
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <button
                      data-section="top-pick-2026"
                      onClick={() => scrollToSection('top-pick-2026')}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 relative ${
                        activeSection === 'top-pick-2026'
                          ? 'bg-primary/5 text-primary border-l-3 border-primary/60 shadow-sm'
                          : 'hover:bg-muted/50 text-muted-foreground hover:text-primary'
                      }`}
                    >
                      Top Pick 2026
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content - Full width on mobile, 3/4 on desktop */}
          <div className="col-span-full lg:col-span-3">
            <main className="space-y-8 sm:space-y-12 lg:ml-4 pt-12 lg:pt-0 px-1 sm:px-0">{/* Add top padding on mobile for the menu button */}

            {/* Introduction Section */}
            <section id="introduction" className="scroll-mt-24 2xl:scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Introduction</h2>
              <div className="prose max-w-none sm:max-w-4xl mb-6 sm:mb-8">
                <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">
                  <a href="https://joinassembly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Learn more about employee recognition software">Employee recognition software</a> has shifted from being a "nice-to-have" perk to a core part of HR strategy. With hybrid and distributed teams, rising retention challenges, and the need to strengthen culture, organizations are turning to <a href="https://www.gallup.com/workplace/236441/employee-recognition.aspx" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Gallup research on employee recognition">recognition</a> platforms to keep employees engaged and connected.
                </p>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  This guide reviews 20 of the top platforms in 2026 — from SMB-friendly tools to enterprise-grade solutions — so you can compare features, pricing, and strengths to find the right fit for your business.
                </p>
              </div>
              
              {/* Q&A Info Panel */}
              <Card className="bg-assembly-orange/5 border-assembly-orange/20 shadow-sm border-l-4 border-l-assembly-orange/60 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-assembly-orange/80 to-assembly-orange/40"></div>
                <CardContent className="p-4 sm:p-6 ml-2">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-assembly-orange/10 rounded-full flex items-center justify-center mt-1">
                        <img 
                          src="/lovable-uploads/a19ed7a0-c63c-4116-a09f-eaf74616b81c.png" 
                          alt="Quote symbol" 
                          className="w-4 h-4 object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">
                          Why should organizations invest in recognition software?
                        </h3>
                        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed italic">
                          Because recognition directly impacts business outcomes: lower turnover, higher engagement, and stronger productivity. Tools like <a href="https://joinassembly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Assembly employee recognition platform">Assembly</a> make it easy to turn recognition into a repeatable process — with peer-to-peer appreciation, automated rewards, and even customer gifting.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Overview Section */}
            <section id="overview" className="scroll-mt-24 2xl:scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Overview</h2>
              <div className="prose max-w-none sm:max-w-4xl">
                <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">
                  <a href="https://joinassembly.com/solutions/recognition" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Learn about Assembly's employee recognition and rewards solutions">Employee recognition and rewards</a> programs aren't just about boosting morale — they deliver measurable business impact. Studies show that:
                </p>
                <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-muted-foreground text-sm sm:text-base leading-relaxed">Companies with strong recognition programs see <strong>31% lower turnover</strong> (<a href="https://www.shrm.org/labs/resources/building-a-connected-workforce-key-insights-on-employee-engagement" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="SHRM research on employee engagement and retention">SHRM</a>).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-muted-foreground text-sm sm:text-base leading-relaxed">Employees recognized for their work are <strong>23% more likely to be engaged</strong> (<a href="https://www.gallup.com/workplace/285674/improve-employee-engagement-workplace.aspx" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Gallup research on employee engagement">Gallup</a>).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-muted-foreground text-sm sm:text-base leading-relaxed">82% of employees say recognition is an important part of job satisfaction (<a href="https://www.apa.org/pubs/reports/work-in-america/2023-workplace-health-well-being" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="APA research on workplace health and well-being">APA</a>).</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mb-6 sm:mb-8 text-sm sm:text-base leading-relaxed">
                  These platforms provide the <a href="https://joinassembly.com/blog/best-practices-for-managing-a-successful-employee-rewards-program" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Best practices for managing a successful employee rewards program">structure</a> to make recognition consistent, scalable, and tied to company values.
                </p>
              </div>
            </section>

            {/* Top 20 Platforms Section */}
            <section id="methodology" className="scroll-mt-24 2xl:scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Top 20 Employee Recognition Platforms in 2026</h2>
              
              {/* Questions Info Panel */}
              <Card className="bg-assembly-orange/5 border-assembly-orange/20 shadow-sm border-l-4 border-l-assembly-orange/60 relative overflow-hidden mb-6 sm:mb-8">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-assembly-orange/80 to-assembly-orange/40"></div>
                <CardContent className="p-4 sm:p-6 ml-2">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-assembly-orange/10 rounded-full flex items-center justify-center mt-1">
                        <img 
                          src="/lovable-uploads/a19ed7a0-c63c-4116-a09f-eaf74616b81c.png" 
                          alt="Quote symbol" 
                          className="w-4 h-4 object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">
                          Questions to Ask Before Choosing a Recognition Platform in 2026
                        </h3>
                        <ul className="space-y-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Does it integrate with our daily tools (Slack, Teams, HRIS/SSO) and is it easy for everyone to use?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Can it reflect our values and reinforce the behaviors that drive results (peer, manager, cross-team)?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Is the employee experience strong for remote, hybrid, frontline, and global teams (mobile, multi-language, localized rewards)?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Do we get diverse reward options (gift cards, swag, donations) that match our workforce?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Are analytics & reporting robust enough to show trends, engagement, and ROI to leaders?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Can it scale with governance (budgets, approvals, audit logs) as we grow?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Does it meet security & compliance needs (GDPR, SOC 2) and protect employee data?</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                            <span>Does it offer AI & automation (nudges, milestone triggers, campaign workflows) to reduce admin work?</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="prose max-w-none sm:max-w-4xl mb-6 sm:mb-8">
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  The following platforms were evaluated using independent reviews (G2, Capterra), feature depth, scalability, customer adoption, and pricing transparency. The order reflects the original source list, starting with Assembly.
                </p>
              </div>
            </section>

            {/* Vendor Rankings */}
            <section className="space-y-8 sm:space-y-12">
              {vendors.map((vendor, index) => (
                <article key={vendor.id} id={vendor.id} className="scroll-mt-24 2xl:scroll-mt-28">
                  <div className="border-b pb-6 sm:pb-8 mb-6 sm:mb-8">
                    <h3 className="text-lg sm:text-2xl font-bold mb-3 sm:mb-4 leading-tight">
                      {vendor.rank}. {vendor.name}
                    </h3>
                    
                      {vendor.name === "Assembly" ? (
                        // Assembly-specific layout
                        <div className="space-y-4 sm:space-y-6">
                          <div className="bg-muted/30 rounded-lg p-4 sm:p-8 text-center mb-4 sm:mb-6">
                          <div className="w-full h-72 sm:h-96 bg-white rounded border shadow-sm overflow-hidden">
                                 <img 
                                   src={vendor.screenshot}
                                   alt={vendor.name + " employee recognition platform interface"} 
                                   className="w-full h-full object-contain"
                                 />
                              </div>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">About</h4>
                            <p className="text-muted-foreground mb-3 sm:mb-4 text-sm sm:text-base leading-relaxed break-words">{vendor.about}</p>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Key Features</h4>
                            <ul className="space-y-1.5 sm:space-y-2">
                              {vendor.keyFeatures.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                                  <span className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {"gifting" in vendor && (
                            <div>
                              <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Gifting</h4>
                              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{vendor.gifting}</p>
                            </div>
                          )}

                          <div>
                            <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Plans & Pricing</h4>
                            {typeof vendor.pricing === "object" && "free" in vendor.pricing && (
                              <div className="space-y-1.5 sm:space-y-2 text-sm sm:text-base">
                                <div className="break-words"><strong>Free plan:</strong> {vendor.pricing.free}</div>
                                <div className="break-words"><strong>Recognition:</strong> {vendor.pricing.recognition}</div>
                                <div className="break-words"><strong>Engagement:</strong> {vendor.pricing.engagement}</div>
                                <div className="break-words"><strong>Enterprise Digital HQ:</strong> {vendor.pricing.enterprise}</div>
                              </div>
                            )}
                          </div>

                          {"whyChoose" in vendor && (
                            <div>
                              <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Why Choose Assembly</h4>
                              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{vendor.whyChoose}</p>
                            </div>
                          )}

                          <div className="text-center flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-2">
                            {"link" in vendor ? (
                              <Button variant="outline" size="lg" className="border-assembly-orange text-assembly-orange hover:bg-assembly-orange hover:text-white transition-all duration-300 shadow-lg hover:shadow-assembly-orange/25 text-sm sm:text-base w-full sm:w-auto" asChild>
                                <a href={vendor.link} target="_blank" rel="noopener noreferrer">
                                  Visit Assembly
                                </a>
                              </Button>
                            ) : (
                              <Button variant="outline" size="lg" className="border-assembly-blue text-assembly-blue hover:bg-assembly-blue hover:text-white transition-all duration-300 text-sm sm:text-base w-full sm:w-auto">
                                Book An Assembly Demo
                              </Button>
                            )}
                            <Button 
                              variant="default" 
                              size="lg"
                              onClick={() => setIsDemoModalOpen(true)}
                              className="bg-gradient-to-r from-assembly-blue to-assembly-blue/90 hover:from-assembly-blue/90 hover:to-assembly-blue text-white shadow-lg hover:shadow-assembly-blue/25 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto"
                            >
                              Book a Demo
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Default vendor layout
                        <div className="space-y-4 sm:space-y-6">
                          {/* Image Placeholder */}
                           <div className="bg-muted/30 rounded-lg text-center mb-4 sm:mb-6">
                             <div className="w-full h-72 sm:h-96 bg-white rounded border shadow-sm overflow-hidden">
                                 <LazyImage
                                   src={vendor.screenshot}
                                   alt={vendor.name + " employee recognition platform interface"} 
                                   className="w-full h-full object-contain"
                                   containerClassName="w-full h-full"
                                 />
                              </div>
                           </div>

                          <div>
                            <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">About</h4>
                            <p className="text-muted-foreground mb-3 sm:mb-4 text-sm sm:text-base leading-relaxed break-words">{vendor.about}</p>
                          </div>

                          {"bestFor" in vendor && (
                            <div>
                              <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Best For</h4>
                              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{vendor.bestFor}</p>
                            </div>
                          )}

                          {"customers" in vendor && vendor.customers && (
                            <div>
                              <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Standout Customers</h4>
                              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{vendor.customers.join(", ")}</p>
                            </div>
                          )}

                          <div>
                            <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Pricing</h4>
                            {typeof vendor.pricing === "string" ? (
                              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{vendor.pricing}</p>
                            ) : typeof vendor.pricing === "object" && vendor.pricing ? (
                              <div className="space-y-1.5 sm:space-y-2 text-sm sm:text-base">
                                {Object.entries(vendor.pricing).map(([key, value]) => (
                                  <div key={key} className="break-words">
                                    <strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</strong> {value}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted-foreground text-sm sm:text-base">Contact sales</p>
                            )}
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 sm:mb-3 text-base sm:text-lg">Key Features</h4>
                            <ul className="space-y-1.5 sm:space-y-2">
                              {vendor.keyFeatures.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-assembly-orange rounded-full mt-2 flex-shrink-0"></div>
                                  <span className="text-muted-foreground text-sm sm:text-base leading-relaxed break-words">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {"link" in vendor && vendor.link ? (
                            <div className="text-center pt-2">
                              <Button variant="outline" size="lg" className="border-assembly-blue text-assembly-blue hover:bg-assembly-blue hover:text-white transition-all duration-300 shadow-lg hover:shadow-assembly-blue/25 text-sm sm:text-base w-full sm:w-auto" asChild>
                                <a href={vendor.link} target="_blank" rel="noopener noreferrer">
                                  🔗 Visit {vendor.name}
                                </a>
                              </Button>
                            </div>
                          ) : (
                            <div className="text-center pt-2">
                              <Button variant="outline" size="lg" className="border-assembly-blue text-assembly-blue hover:bg-assembly-blue hover:text-white transition-all duration-300 shadow-lg hover:shadow-assembly-blue/25 text-sm sm:text-base w-full sm:w-auto">
                                Book A Demo
                              </Button>
                            </div>
                          )}
                        </div>
                    )}
                  </div>
                </article>
              ))}
            </section>

            {/* Our Top Pick Section */}
            <section id="top-pick-2026" className="mt-12 sm:mt-16 scroll-mt-24 2xl:scroll-mt-28">
              <Card className="bg-gradient-to-br from-assembly-blue/10 via-white to-assembly-orange/10 border-assembly-blue/30 shadow-2xl">
                <CardContent className="p-4 sm:p-8">
                  <div className="space-y-4 sm:space-y-6">
                    <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Our Top Pick for 2026</h2>
                    
                    <div className="prose max-w-none">
                      <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">
                        All 20 platforms on this list offer valuable solutions, but one consistently rises above the rest: <strong><a href="https://joinassembly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" title="Assembly - Employee Recognition and Rewards Platform">Assembly</a></strong>.
                      </p>
                      
                      <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">
                        With the <strong>highest G2 rating in the category (4.9/5 from 3,000+ reviews)</strong>, flexible plans (including a free option), and support for both employee recognition and customer gifting, Assembly is the platform employees actually enjoy using.
                      </p>
                      
                      <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">
                        Recognition isn't just about saying thank you — it drives retention, engagement, and productivity. For organizations looking for a proven solution in 2026, <strong>Assembly is the clear place to start</strong>.
                      </p>
                      
                      <div className="text-center mt-6 sm:mt-8">
                        <Button 
                          size="lg" 
                          className="bg-gradient-to-r from-assembly-blue to-assembly-blue/90 hover:from-assembly-blue/90 hover:to-assembly-blue text-white px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-lg rounded-xl shadow-lg hover:shadow-assembly-blue/25 transition-all duration-300 w-full sm:w-auto"
                          asChild
                        >
                          <a href="https://www.joinassembly.com/" target="_blank" rel="noopener noreferrer">
                            👉 Get started with Assembly today
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Newsletter Signup */}
            <section className="mt-16">
              <Card className="bg-gradient-to-br from-assembly-blue/5 via-white to-assembly-orange/5 border-assembly-blue/20 shadow-2xl hover:shadow-assembly-blue/10 transition-all duration-500">
              </Card>
            </section>
            </main>
          </div>
        </div>
      </div>

      <Footer />
      
      <DemoBookingModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
    </div>
  );
};

export default Top20Rankings2025;